import * as yup from "yup";
import React, {useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useFormik} from "formik";
import {Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import NoSearchResult from "../../../components/Common/NoSearchResult";
import {IPermissionForm, IPermissionGroupPagination} from "../../../store/permissions/types";
import {PermissionsService} from "../../../services/permissions.service";
import Select from "../../../components/Common/Select";
import {components} from "react-select";

interface ModalPermissionProps {
    isOpen: boolean;
    permissionId: number | null;
    selectedGroupId?: number | null;
    onClose: () => void;
}

const SingleValue = ({children, ...props}: any) => (
    <components.SingleValue className="d-flex align-items-center gap-2" {...props}>
        {props.data.icon && <i className={`bx bx-${props.data.icon} fs-5`}/>}
        {children}
    </components.SingleValue>
);

const Option = (props: any) => (
    <components.Option className="d-flex align-items-center gap-3" {...props}>
        {props.data.icon && <i className={`bx bx-${props.data.icon} fs-5`}/>}
        {props.children}
    </components.Option>
);

const ModalPermission: React.FC<ModalPermissionProps> = ({isOpen, permissionId, selectedGroupId, onClose}) => {
    const isEditModal = permissionId !== null;
    const queryClient = useQueryClient();

    const [savedPermission, setSavedPermission] = useState<boolean>(false);


    const {
        data: permission,
        isFetching
    } = useQuery({
        queryKey: ["keyPermission", permissionId],
        queryFn: () => PermissionsService.getPermissionById(permissionId || -1),
        select: (p: any) => p.data,
        enabled: isEditModal,
    });

    const selectGroups = (groups: IPermissionGroupPagination) =>
        groups.data.map((group) => ({label: group.name, value: group.id, icon: group.icon}))

    const {data: groupsSelect, isFetching: isFetchingGroups} = useQuery({
        queryKey: ["keyPermissionsGroups"],
        queryFn: () => PermissionsService.getGroupsAll(),
        select: selectGroups
    });

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyPermissionsGroups"]})
            .then(() => onClose())
    }

    const {mutate: createPermission, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: IPermissionForm) => PermissionsService.createPermission(data),
        onSuccess: onSuccess,
        mutationKey: ["keyCreatePermission"],
    });

    const {mutate: updatePermission, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: IPermissionForm) => PermissionsService.updatePermission(permissionId!, data),
        onSuccess: onSuccess,
        mutationKey: ["keyUpdatePermission", permissionId],
    });

    const validationSchema: yup.Schema<IPermissionForm> = yup.object().shape({
        title: yup.string()
            .min(1, "min 1 character")
            .max(25, "max 25 characters")
            .required("Permission title is required"),

        name: yup.string()
            .min(1, "min 1 character")
            .required("Permission name is required"),

        description: yup.string()
            .max(100, "max 100 characters"),

        groupId: yup.number()
            .min(0, "Permission group is required")
            .required("Permission group is required")
    });

    const initialValues: IPermissionForm = {
        name: permission?.name || "",
        title: permission?.title || "",
        description: permission?.description || "",
        groupId: (permission as any)?.group_id || selectedGroupId || -1
    }

    const handleSubmitForm = (result: IPermissionForm) => {
        setSavedPermission(true);

        if (!isEditModal) createPermission(result);
        else updatePermission(result);
    };

    const {
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
        errors,
        touched,
        isValid,
        resetForm,
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const closeModal = () => {
        onClose();
        resetForm();
    };

    const handleChangeGroup = (select: any) => {
        setFieldValue("groupId", select.value)
    }

    const selectedGroup = groupsSelect?.find((group) => group.value === values.groupId)

    const isPendingSave = isPendingCreate || isPendingUpdate || (!!queryClient.isFetching() && savedPermission);

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal} tag="h4">
                {isEditModal ? "Edit" : "Add"} Permission
            </ModalHeader>
            {isFetching ? <div className="my-4"><NoSearchResult text="Loading..."/></div> : (
                <form onSubmit={handleSubmit} className="needs-validation">
                    <ModalBody>
                        <div className="mb-3">
                            <Label htmlFor="title" className="mb-1 ms-1">Title*</Label>
                            <Input
                                placeholder="Enter permission title..."
                                name="title"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.title}
                                hasError={!!(errors.title && touched.title)}
                                errorText={errors.title}
                            />
                        </div>
                        <div className="mb-3">
                            <Label htmlFor="name" className="mb-1 ms-1">Name*</Label>
                            <Input
                                placeholder="your-permission-name..."
                                name="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                hasError={!!(errors.name && touched.name)}
                                errorText={errors.name}
                            />
                        </div>
                        <div className="mb-3">
                            <Label htmlFor="description" className="mb-1 ms-1">Description</Label>
                            <Input
                                placeholder="Enter permission description..."
                                name="description"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.description}
                                hasError={!!(errors.description && touched.description)}
                                errorText={errors.description}
                            />
                        </div>
                        <div>
                            <Label htmlFor="group" className="mb-1 ms-1">Group*</Label>
                            <Select
                                isSearchable
                                name="group"
                                label="Group"
                                placeholder="Select an group"
                                components={{SingleValue, Option}}
                                options={groupsSelect}
                                onChange={handleChangeGroup}
                                value={selectedGroup}
                                isLoading={isFetchingGroups}
                                isDisabled={isFetchingGroups || isPendingSave}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <div className="w-100 d-flex justify-content-between align-items-center flex-row-reverse">
                            <Button type="submit" color="success" disabled={!isValid || isPendingSave}>
                                {isPendingSave ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </ModalFooter>
                </form>
            )}
        </Modal>
    )
}

export default ModalPermission;